import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';

import { Address } from '../../models';
import { constructDisplayLabel } from '../../helpers';
import {
  AddressTranslationAPIResponse,
  FailureResponse,
  IOriginAddressTranslationAPIResponse,
  ParsedAddressResponse,
} from './models';
import { IDapiResponse } from '@origin-digital/daxi';
import { dapiFetchWithLogging } from '@origin-digital/sales-common-types-fns';

export const failureResponse: FailureResponse = {
  success: false,
};

/**
 *
 * @param response - an object derived from the Digital commons API for address Id
 * @returns {object} - an address object with properties such as unit, streetNumber, StreetName, City and also a label
 * that concatenates the the properties into a string
 */
export const parseAddressByIdResponse = (response: any): ParsedAddressResponse => {
  if (!response || !response.data) {
    return failureResponse;
  }

  const {
    RoomNo: unit,
    HouseNo: streetNumber,
    Street: streetName,
    City: city,
    Region: state,
    PostalCode: postcode,
  } = response.data;

  const address: Address = {
    dpid: undefined,
    unit,
    streetNumber,
    streetName,
    city,
    state,
    postcode,
  };

  const label = constructDisplayLabel(address);

  return {
    success: true,
    ...address,
    label,
  };
};

/**
 * Given a Origin recognised addressId (e.g. NGW0000802226), this method will parse the address into an address
 * object with properties such as unit, streetNumber, StreetName, City, etc.
 * @param {string} url - Address commons digital API endpoint
 * @param {string} addressId - An Oracle recognised addresssId
 *
 * @returns {Promise.<QASAddress>}
 */
export const getAddressFromSapId = async (
  url: string,
  addressId: string,
  headers?: Headers
): Promise<ParsedAddressResponse> => {
  if (!addressId) {
    throw new ReferenceError('Missing required parameter `addressId`');
  }

  if (typeof addressId !== 'string') {
    throw new TypeError('Parameter `addressId` was not of required type String');
  }

  const config: AxiosRequestConfig = {
    method: 'GET',
    url,
    params: { addressId },
  };

  if (headers) {
    config.headers = Object.assign(config.headers || {}, headers);
  }

  const response = await axios(config);

  return parseAddressByIdResponse(response.data);
};

/**
 * Returns a promise resolving to the SAP address ID for a given address.
 *
 * @param url - The API url
 * @param _authKey - Deprecated: no longer used. Kept temporarily for backward compatibility.
 * @param params - The request parameters
 */
export const getSapIdFromAddress = async (
  url: string,
  _authKey?: string,
  params?: Address,
  headers?: Headers
): Promise<AddressTranslationAPIResponse> => {
  const config: AxiosRequestConfig = {
    method: 'GET',
    url,
    params,
  };

  if (headers) {
    config.headers = Object.assign(config.headers || {}, headers);
  }

  const response = await axios(config);

  if (!response) {
    throw new Error('Missing response');
  }

  return response as unknown as AddressTranslationAPIResponse;
};

/**
 * Returns Origin addresses matching the given address params.
 *
 * @param url - The API url
 * @param _authKey - Deprecated: no longer used. Kept temporarily for backward compatibility.
 * @param params - The request parameters
 */
export const getOriginAddresses = async (
  url: string,
  _authKey?: string,
  params?: Address,
  headers?: Record<string, string>
): Promise<AxiosResponse<IOriginAddressTranslationAPIResponse>> => {
  const config: AxiosRequestConfig = {
    method: 'POST',
    url,
    params,
  };

  if (headers) {
    config.headers = Object.assign(config.headers || {}, headers);
  }

  const response = await axios(config);

  const responseIsOk = response.status >= 200 && response.status <= 299;

  if (!response || !responseIsOk) {
    throw new Error('Missing response or response is not successful');
  }

  return response;
};

export const getOriginAddressesFromBatchId = async (
  url: string,
  batchId: string,
  headers?: Record<string, string>
): Promise<Address[]> => {
  if (!batchId) {
    throw new ReferenceError('Missing required parameter `batchId`');
  }

  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${url}/${batchId}`,
  };

  if (headers) {
    config.headers = Object.assign(config.headers || {}, headers);
  }

  const response = await axios(config);

  return response.data.map(parseAddressByOriginAddressIdResponse);
};

export const getAddressFromOriginAddressId = async (
  url: string,
  addressId: string,
  headers?: Record<string, string>
): Promise<Address> => {
  if (!addressId) {
    throw new ReferenceError('Missing required parameter `addressId`');
  }

  if (typeof addressId !== 'string') {
    throw new TypeError('Parameter `addressId` was not of required type String');
  }

  const config: AxiosRequestConfig = {
    method: 'GET',
    url: `${url}/${addressId}`,
  };

  if (headers) {
    config.headers = Object.assign(config.headers || {}, headers);
  }

  const response = await axios(config);

  return parseAddressByOriginAddressIdResponse(response.data);
};

export const parseAddressByOriginAddressIdResponse = (response: any): Address => {
  const address: Address = {
    allotment: response.lotNumber,
    originAddressId: response.addressId,
    dpid: undefined,
    unit: response.flatUnitNumber
      ? `${response.flatUnitType} ${response.flatUnitNumber}`
      : undefined,
    streetNumber: response.houseNumber || response.lotNumber,
    streetName: response.streetName + ' ' + response.streetType,
    streetTypeSuffix: response.streetTypeSuffix,
    city: response.suburbLocality,
    state: response.state,
    postcode: response.postcode,
    nmi: response.nmi,
    nmiStatus: response.nmiStatus,
    nmiRating: response.nmiRating,
    mirn: response.mirn,
    mirnStatus: response.mirnStatus,
    mirnRating: response.mirnRating,
    isExactMatchForNMI: response.isExactMatchForNMI,
    isExactMatchForMIRN: response.isExactMatchForMIRN,
    isSolarAvailable: response.isSolarAvailable,
    isSmartMeter: response.isSmartMeter,
    batchId: response.batchId,
    addressLabel: response.addressLabel,
    customerClass: response.customerClass,
    isSelected: response.isSelected,
    feederClass: response.feederClass,
    elecDetails: response.elecDetails,
  };

  const label = constructDisplayLabel(address);

  return {
    ...address,
    label,
  };
};

export const updateAddressSelected = (
  url: string,
  addressId: string,
  isSelected = true
): Promise<IDapiResponse<Address[]>> => {
  const config = {
    method: 'PATCH',
  };

  const constructedURL = `${url}?addressId=${addressId}&isSelected=${isSelected}`;

  return dapiFetchWithLogging<Address[]>(constructedURL, config);
};
